import Axios from "axios";
import {
  DefaultButton,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  Text,
  TextField,
} from "@fluentui/react";
import React, { FormEvent, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../contexts/AuthContext";
import {
  accountEmailDisplayValue,
  isMissingAccountEmail,
} from "../helpers/AccountEmailHelper";
import {
  accountActionError,
  accountActionMessage,
  AccountActionResult,
} from "../helpers/AccountActionHelper";
import { TitleAndContentLayout } from "./TitleAndContentLayout";

interface IUserDetails {
  userName: string;
  email: string;
}

export const UserSettingsProfile = () => {
  const { UserInfo, Logout, FetchUserInfo } = useContext(AuthContext);
  const [details, setDetails] = useState<IUserDetails>({
    email: "",
    userName: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();
  const { t } = useTranslation();
  const user = UserInfo?.user;
  const detailsChanged =
    details.userName.trim() !== user?.userName ||
    details.email.trim() !== accountEmailDisplayValue(user?.email);
  const missingEmail = isMissingAccountEmail(user?.email);

  useEffect(() => {
    if (user) {
      setDetails({
        userName: user.userName ?? "",
        email: accountEmailDisplayValue(user.email),
      });
    }
  }, [user]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (loading || !detailsChanged) return;
    if (isMissingAccountEmail(details.email)) {
      setError("Upišite vlastiti email kojem imate pristup.");
      return;
    }
    setError(undefined);
    setMessage(undefined);
    setLoading(true);
    try {
      const nextDetails = {
        userName: details.userName.trim(),
        email: details.email.trim(),
      };
      await Axios.post("UserManagement/changedetails", nextDetails);
      if (nextDetails.userName !== user?.userName) {
        await Logout();
        alert(t("login_again_with_new_user_details"));
      } else {
        setDetails(nextDetails);
        await FetchUserInfo();
        setMessage(
          "Email je spremljen. Ako još nije potvrđen, pošaljite poveznicu za potvrdu i otvorite je iz svojeg emaila.",
        );
      }
    } catch (requestError) {
      setError(
        accountActionError(
          requestError,
          "Promjene nije moguće spremiti. Provjerite email i pokušajte ponovno.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const sendVerification = async () => {
    if (loading) return;
    setError(undefined);
    setMessage(undefined);
    setLoading(true);
    try {
      const { data } = await Axios.post<AccountActionResult>(
        "AccountEmail/send-verification",
      );
      setMessage(
        accountActionMessage(
          data,
          "Poveznica za potvrdu poslana je na spremljeni email. Provjerite i neželjenu poštu.",
        ),
      );
    } catch (requestError) {
      setError(
        accountActionError(
          requestError,
          "Poveznicu nije moguće poslati. Pokušajte ponovno.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <TitleAndContentLayout title="profile" lessPadding isSubtitle pad>
      <Stack tokens={{ childrenGap: 12 }} styles={{ root: { maxWidth: 420 } }}>
        <Text>
          Upišite vlastiti email kojem imate pristup. Nakon potvrde možete sami
          obnoviti zaboravljenu lozinku za Onix i OnixPhone.
        </Text>
        {missingEmail ? (
          <MessageBar messageBarType={MessageBarType.warning}>
            Email nije postavljen. Pomoćna zajednička adresa ne može se
            koristiti za oporavak lozinke.
          </MessageBar>
        ) : user?.emailConfirmed ? (
          <MessageBar messageBarType={MessageBarType.success}>
            Email je potvrđen.
          </MessageBar>
        ) : (
          <MessageBar messageBarType={MessageBarType.warning}>
            Email još nije potvrđen. Pošaljite poveznicu za potvrdu i otvorite
            je iz svojeg emaila.
          </MessageBar>
        )}
        <form onSubmit={submit}>
          <Stack tokens={{ childrenGap: 10 }}>
            <TextField
              label={t("username")}
              name="userName"
              value={details.userName}
              disabled={loading}
              required
              onChange={(_, value) =>
                setDetails({ ...details, userName: value ?? "" })
              }
            />
            <TextField
              type="email"
              label={t("email")}
              name="email"
              value={details.email}
              autoComplete="email"
              disabled={loading}
              required
              onChange={(_, value) =>
                setDetails({ ...details, email: value ?? "" })
              }
            />
            {detailsChanged && (
              <PrimaryButton
                type="submit"
                text={t("save_changes")}
                disabled={loading}
              />
            )}
          </Stack>
        </form>
        {!missingEmail && !user?.emailConfirmed && (
          <DefaultButton
            text="Pošalji poveznicu za potvrdu emaila"
            onClick={sendVerification}
            disabled={loading || detailsChanged}
          />
        )}
        {error && (
          <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
        )}
        {message && (
          <MessageBar messageBarType={MessageBarType.success}>
            {message}
          </MessageBar>
        )}
      </Stack>
    </TitleAndContentLayout>
  );
};
